import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { date, mixed, number, object, string } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { UsuarioAlterarSenha } from 'Component/Admin/Usuario';
import { useAlerta, useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, {
  Arquivo,
  Botao as BotaoForm,
  Campo,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Assinatura from 'Interface/Assinaturas';
import { Erro } from 'Interface/Requisicao';
import Usuario from 'Interface/Usuarios';
import {
  useEditarAssinaturaMutation,
  useEditarUsuarioMutation,
  useRecuperarAssinaturasQuery,
  useRecuperarUsuarioQuery,
} from 'Service';
import {
  ESTADOS,
  IMAGENS,
  MASCARA_CPF,
  MASCARA_TELEFONE,
  PERMISSOES,
} from 'View/constants';
import { errosParaLista } from 'View/functions';

import Matriculas from './Matriculas';
import Transacoes from './Transacoes';

const regras = object({
  nome: string().required('Obrigatório'),
  email: string().email('Inválido').required('Obrigatório'),
  cpf: string().required('Obrigatório'),
  rg: string().nullable().optional(),
  orgao_emissor: string().nullable().optional(),
  estado_orgao_emissor: string().nullable().optional(),
  telefone1: string().required('Obrigatório'),
  telefone2: string().nullable().optional(),
  nascimento: date().nullable().optional(),
  cep: string().nullable().optional(),
  estado: string().nullable().optional(),
  cidade: string().nullable().optional(),
  bairro: string().nullable().optional(),
  endereco: string().nullable().optional(),
  numero: number().nullable().integer().positive('Inválido').optional(),
  complemento: string().nullable().optional(),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return IMAGENS.includes(valor.type);
      }
      return true;
    })
    .optional(),
  permissao: string()
    .is(['super', 'admin', 'comum', 'conteudista'])
    .required('Obrigatório'),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const alerta = useAlerta();

  const { id = 0 } = useParams();
  const { data, isLoading } = useRecuperarUsuarioQuery(id);

  const [aba, setAba] = useState<number>(0);
  const [modalAlterarSenha, setModalAlterarSenha] = useState<boolean>(false);
  const [editarUsuario] = useEditarUsuarioMutation();
  const [editarAssinatura] = useEditarAssinaturaMutation();

  const {
    data: { assinaturas: [assinatura] } = { assinaturas: [{} as Assinatura] },
    refetch,
  } = useRecuperarAssinaturasQuery({
    contain: ['Planos'],
    options: { usuario_id: id },
    scope: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refetch, []);

  const enviar = async (dados: Usuario): Promise<void> => {
    try {
      await editarUsuario(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao('/admin/usuarios');
    } catch (error) {
      alerta({
        titulo: 'Erro ao editar',
        conteudo: errosParaLista(error as Erro),
        tema: 'danger',
      });
    }
  };

  const cancelar = async (): Promise<void> => {
    try {
      await editarAssinatura({ id: assinatura.id, status: 0 }).unwrap();

      toast({ conteudo: 'Assinatura cancelada com sucesso!', tema: 'success' });
    } catch (error) {
      toast({
        titulo: 'Erro ao cancelar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo="Editando usuário"
        descricao="Altere o formulário e clique em enviar para editar o usuário"
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-1 vstack flex-row flex-xxl-column gap-2 mb-3">
          <Botao
            icone="house"
            tema={aba == 0 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(0)}
          />
          <Botao
            icone="lock-fill"
            tema="outline-primary"
            onClick={() => setModalAlterarSenha(true)}
          />
          <Botao
            icone="book"
            tema={aba == 1 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(1)}
          />
          <Botao
            icone="cash-coin"
            tema={aba == 2 ? 'primary' : 'outline-primary'}
            onClick={() => setAba(2)}
          />
        </div>
        <div className="col-xl-12 col-xxl-11">
          {aba == 0 && (
            <Form
              onSubmit={enviar}
              inicial={data?.usuario}
              desabilitado={isLoading}
              regras={regras}
            >
              <div className="row">
                <div className="col-xl-8 col-xxl-9">
                  <div className="bg-white shadow-sm rounded p-4">
                    <div className="row">
                      <div className="col-md-12">
                        <Campo name="nome" rotulo="Nome" />
                      </div>
                      <div className="col-md-12">
                        <Campo
                          name="email"
                          rotulo="E-mail"
                          prefixar={<Icone nome="envelope-fill" />}
                        />
                      </div>
                      <div className="col-md-3">
                        <Campo name="cpf" rotulo="CPF" mascara={MASCARA_CPF} />
                      </div>
                      <div className="col-md-3">
                        <Campo name="rg" rotulo="RG" />
                      </div>
                      <div className="col-md-3">
                        <Campo name="orgao_emissor" rotulo="Orgão emissor" />
                      </div>
                      <div className="col-md-3">
                        <Campo
                          name="estado_orgao_emissor"
                          rotulo="Estado do orgão emissor"
                        />
                      </div>
                      <div className="col-md-4">
                        <Campo
                          name="telefone1"
                          rotulo="Telefone 1"
                          mascara={MASCARA_TELEFONE}
                          prefixar={<Icone nome="telephone-fill" />}
                        />
                      </div>
                      <div className="col-md-4">
                        <Campo
                          name="telefone2"
                          rotulo="Telefone 2"
                          mascara={MASCARA_TELEFONE}
                          prefixar={<Icone nome="telephone-fill" />}
                        />
                      </div>
                      <div className="col-md-4">
                        <Campo
                          name="nascimento"
                          type="date"
                          rotulo="Nascimento"
                          prefixar={<Icone nome="calendar-fill" />}
                        />
                      </div>
                      <div className="col-md-4">
                        <Campo
                          name="cep"
                          rotulo="CEP"
                          prefixar={<Icone nome="geo-fill" />}
                        />
                      </div>
                      <div className="col-md-4">
                        <Selecao
                          name="estado"
                          rotulo="Estado onde reside"
                          opcoes={ESTADOS}
                        />
                      </div>
                      <div className="col-md-4">
                        <Campo name="cidade" rotulo="Cidade onde reside" />
                      </div>
                      <div className="col-md-4">
                        <Campo name="bairro" rotulo="Bairro onde reside" />
                      </div>
                      <div className="col-md-6">
                        <Campo
                          name="endereco"
                          rotulo="Endereço de onde reside"
                        />
                      </div>
                      <div className="col-md-2">
                        <Campo name="numero" rotulo="Número" />
                      </div>
                      <div className="col-md-12">
                        <Campo
                          name="complemento"
                          rotulo="Complemento"
                          semMargem
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-xxl-3">
                  <div className="bg-white shadow-sm rounded p-4">
                    <div className="mb-5 d-flex flex-column justify-content-center align-items-center">
                      <Imagem
                        src={data?.usuario.imagem}
                        alt={data?.usuario.nome}
                        className="rounded-circle object-fit-cover shadow-lg"
                        width="100"
                        height="100"
                      />
                      {assinatura && (
                        <>
                          <h4 className=" mt-3">
                            {assinatura.plano?.nome}
                            {assinatura.status == 1 && (
                              <Botao
                                tema="transparent"
                                icone="trash"
                                className="text-danger"
                                confirmacao="Tem certeza que deseja cancelar a assinatura desse usuário?"
                                onClick={cancelar}
                              />
                            )}
                          </h4>
                          {assinatura.status == 1 ? (
                            <small className="text-muted text-center">
                              Próxima cobrança
                              <br />
                              <strong>
                                {assinatura.proxima_cobranca
                                  .data()
                                  .toLocaleString()}
                              </strong>
                            </small>
                          ) : (
                            <small className="text-danger text-center">
                              Desativada
                            </small>
                          )}
                        </>
                      )}
                    </div>
                    <Arquivo name="imagem" />
                    <Selecao
                      name="permissao"
                      rotulo="Permissão"
                      prefixar={<Icone nome="lock-fill" />}
                      opcoes={PERMISSOES}
                    />

                    <div className="mt-5">
                      <BotaoForm>Enviar</BotaoForm>
                      <BotaoLink
                        tema="light"
                        to="/admin/usuarios"
                        className="ms-2"
                      >
                        Cancelar
                      </BotaoLink>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
          {aba == 1 && <Matriculas />}
          {aba == 2 && <Transacoes />}
        </div>
      </div>
      <UsuarioAlterarSenha
        exibir={modalAlterarSenha}
        aoFechar={() => setModalAlterarSenha(false)}
        usuarioId={data?.usuario.id}
      />
    </div>
  );
};

export default Editar;
